import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, EMPTY, map, Observable, throwError } from "rxjs";
import { Router } from "@angular/router";
import { EnvironmentService } from "../utility/environment.service";

export interface User{
    email: string;
    mobileNumber: string;
    name: string;
    roldId: number;
    roleName: string;
    userId: number;
    roleDatasetPermissions: object;
    organization: Organization;
}

interface Organization{
    iconUrl: string;
    name: string;
    id: string;
    type: string;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    readonly rbac: string;
    private _sessionToken: string = "";
    private headers = {};
    
    private _user!: User;
    private userSubject = new BehaviorSubject<User | null>(null);
    user$ = this.userSubject.asObservable();
    private _applicableWarehousesCodes = [];
    
    private navigationModulesSubject = new BehaviorSubject<object>({});
    public navigationModules$: Observable<object> = this.navigationModulesSubject.asObservable();

    constructor(
        private http: HttpClient,
        private router: Router,
        private environmentService: EnvironmentService
    ) {
        this.rbac = this.environmentService.getRbacUrl();
     }

    setSessionToken(userToken: string) {
        this._sessionToken = userToken;
        this.headers = new HttpHeaders().set('Authorization', this._sessionToken);
        this.decrytToken()?.subscribe({
            next: async (data) => {
                this.setUser(data);
                await this.fetchUserAccessControl();
                this.fetchUserAccessModule();
                this.userSubject.next(this._user);
            },
            error: (error) => {
                this.navigateToLoginPage();
            }
        });
    }

    get sessionToken(){
        return this._sessionToken;
    }

    getToken(): string{
        return this._sessionToken;
    }

    get user(): User{
        return this._user;
    }

    private setUser(userData:any) {
        let organization:Organization = {
            iconUrl: userData.organization['orgIconUrl'],
            name: userData.organization['name'],
            id: userData.organization['id'],
            type: userData.organization['type']['name'],
        };

        this._user = {
            email: userData.email,
            mobileNumber: userData.mobileNumber,
            name: userData.name,
            roldId: userData.roleId,
            roleName: userData.roleName,
            userId: userData.userId,
            roleDatasetPermissions: userData.roleDatasetPermissions,
            organization: organization
        } as User;
    }

    private navigateToLoginPage(){
        // todo : redirect to login page
    }

    handleError(error: HttpErrorResponse): Observable<never> {
        if (error.status === 401) {
            this.router.navigateByUrl('/login');
        }

        return throwError(() =>
            error.status === 500 ? 'Internal Server Error' : error
        );
    }

    private decrytToken(): Observable<any> | undefined {
        if (!this._sessionToken) return;
        return this.http.get<any>(
            `${this.rbac}/rbac/api/v1/token/decrypt`,
            { headers: this.headers }
        ).pipe(
            map((data: any) => {
                return data;
            }),
            catchError(this.handleError.bind(this))
        );
    }

    private fetchUserAccessModule(): void {
        if (!this._sessionToken) return;
        
        this.http
            .post<any>(`${this.rbac}/rbac/api/v1/modules/_search`, {}, {
                headers: this.headers
            })
            .pipe(
                catchError(this.handleError.bind(this))
            )
            .subscribe({
                next: (data) => {
                    this.setUserAccessModules(data);
                }
            });
    }

    private setUserAccessModules(modules:any){
        this.navigationModulesSubject.next(modules);
    }

    private setUserAccessControls(accessControls: any) {
        if (accessControls.length) {
            for (let property of accessControls) {
                if (property['propertyType'] == "WAREHOUSE") {
                    this._applicableWarehousesCodes = property['propertyIds'];
                }
            }
        }
    }

    private getUserAccessControl(){
        if (!this._sessionToken) return;
        return this.http
        .get<any>(`${this.rbac}/rbac/api/v1/user/${this._user.userId}/access-controls`, {
            headers: this.headers
        })
        .pipe(
            catchError(this.handleError.bind(this))
        );
    }

    private fetchUserAccessControl() {
        return new Promise( resolve => {
            this.getUserAccessControl()!
                .subscribe({
                    next: (data) => {
                        this.setUserAccessControls(data);
                        resolve(true);
                    },
                    error: (err) => {
                        resolve(false);
                    }
                });
        });

    }

    get applicableWarehousesCodes() {
        return this._applicableWarehousesCodes;
    }

    getOrgUsers(orgId: string, payload:any){
        return this.http
        .post<any>(`${this.rbac}/rbac/api/v1/org/${orgId}/users/_search`, payload, {
            headers: this.headers
        })
        .pipe(
            catchError(this.handleError.bind(this))
        );
    }

}